import { GridCellParams } from '@mui/x-data-grid-pro';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { format } from 'date-fns';
import { dateColumnOperators, standardOperators, tagsOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { getUserAccountById } from '../../../store/User';
import { addressComponentColumns } from '../../Compounds/UpstackDataGrid/addressColumns';
import { tagsColumn } from '../../Compounds/UpstackDataGrid/tagColumns';
import { EntityTagRenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/EntityTagRenderer';
import type { SupportedTagEntities } from '../../../store/Tags';
import { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';
import { SupportCasesItem } from '../../../types';

// Lazy on purpose: a static import closes the col-def ↔ store/User cycle and hands out a half-built store.
const refetchCases = async (id: string): Promise<void> =>
  (await import('../../../store/SupportCase')).fetchCaseById(id);

// Not `account_id`: a case row names its account in Salesforce's id space, and tag ownership is in the portal's.
const caseTagAccountId = (row: SupportedTagEntities): string | undefined =>
  (row as SupportCasesItem).account_upstack_id;

export const DefaultCaseGridColumnDefs: GroupedGridColDef[] = [
  {
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
      />
    ),
    hideable: false,
    disableReorder: true
  },
  {
    field: 'sfId',
    headerName: 'Id',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'account_id',
    headerName: 'Account',
    sortable: true,
    width: 300,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value, row) => {
      return row?.accountName || getUserAccountById(value)?.name || value;
    },
    valueGetter: (value, row) => row?.accountName || getUserAccountById(value)?.name || value,
    disableReorder: true,
    filterable: true,
    filterOperators: standardOperators()
  },
  {
    field: 'caseNumber',
    headerName: 'Case Number',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'subject',
    headerName: 'Subject',
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'additionalRecipients',
    headerName: 'Additional Recipients',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'caseType',
    headerName: 'Case Type',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'issue',
    headerName: 'Issue',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'submittedBy',
    headerName: 'Submitted By',
    sortable: true,
    width: 380,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'dateTimeOpened',
    headerName: 'Date/Time Opened',
    sortable: true,
    width: 180,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value) => {
      return value ? format(value, 'MM/dd/yyyy') : '';
    },
    filterOperators: dateColumnOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'dateTimeClosed',
    headerName: 'Date/Time Closed',
    sortable: true,
    width: 180,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value) => {
      return value ? format(value, 'MM/dd/yyyy') : '';
    },
    filterOperators: dateColumnOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'supplierCaseNumber',
    headerName: 'Supplier Case #',
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'description',
    headerName: 'Description',
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  ...addressComponentColumns(standardOperators()),
  tagsColumn({
    filterOperators: tagsOperators(),
    renderer: EntityTagRenderer,
    entityType: SupportedTagEntityTypes.CASE,
    entityAccountId: caseTagAccountId,
    refetch: refetchCases
  })
];
